import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { teachersApi } from '../lib/api';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { BookOpen, Plus, Pencil, Trash2, Mail, Phone, Award, Eye, EyeOff } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';

const sectionOptions = [
  { id: 'pdv', label: 'Unità Locali' },
  { id: 'documents', label: 'Documenti' },
  { id: 'training', label: 'Formazione' },
  { id: 'safety-checks', label: 'Controlli Sicurezza' },
  { id: 'calendar', label: 'Calendario' },
  { id: 'virtual-classrooms', label: 'Aule Virtuali' },
];

export default function TeachersPage() {
  const { user } = useAuth();
  // Admin/RSPP vedono i docenti in sola lettura: i docenti vengono creati
  // dalla pagina "Utenti" (creando un utente con ruolo docente). Qui possono
  // solo visualizzarli.
  const isReadOnly = ['admin', 'amministratore', 'rspp'].includes(user?.role);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    specialization: '',
    bio: '',
    allowed_sections: []
  });

  useEffect(() => {
    loadTeachers();
  }, []);

  const loadTeachers = async () => {
    try {
      const response = await teachersApi.getAll();
      setTeachers(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei docenti');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      specialization: '',
      bio: '',
      allowed_sections: []
    });
    setEditingTeacher(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTeacher) {
        await teachersApi.update(editingTeacher.id, formData);
        toast.success('Docente aggiornato');
      } else {
        await teachersApi.create(formData);
        toast.success('Docente creato');
      }
      setDialogOpen(false);
      resetForm();
      loadTeachers();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleEdit = (teacher) => {
    setEditingTeacher(teacher);
    setFormData({
      name: teacher.name || '',
      email: teacher.email || '',
      phone: teacher.phone || '',
      specialization: teacher.specialization || '',
      bio: teacher.bio || '',
      allowed_sections: teacher.allowed_sections || []
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Sei sicuro di voler disattivare questo docente?')) return;
    try {
      await teachersApi.delete(id);
      toast.success('Docente disattivato');
      loadTeachers();
    } catch (error) {
      toast.error('Errore nella disattivazione');
    }
  };

  const toggleSection = (sectionId) => {
    setFormData(prev => ({
      ...prev,
      allowed_sections: prev.allowed_sections.includes(sectionId)
        ? prev.allowed_sections.filter(s => s !== sectionId)
        : [...prev.allowed_sections, sectionId]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl">Gestione Docenti</h1>
          <p className="text-muted-foreground mt-1">
            {isReadOnly
              ? 'Visualizzazione docenti. Per crearne uno nuovo, vai in Utenti.'
              : 'Gestisci i docenti e i loro permessi di accesso'}
          </p>
        </div>
        {!isReadOnly && (
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-teacher-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Docente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingTeacher ? 'Modifica Docente' : 'Nuovo Docente'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nome Completo *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="teacher-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefono</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Specializzazione</Label>
                <Input
                  value={formData.specialization}
                  onChange={(e) => setFormData({...formData, specialization: e.target.value})}
                  placeholder="es. Sicurezza sul lavoro, Antincendio..."
                />
              </div>
              <div className="space-y-2">
                <Label>Biografia</Label>
                <Textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({...formData, bio: e.target.value})}
                  placeholder="Breve descrizione del docente..."
                  rows={3}
                />
              </div>
              
              <div className="space-y-3">
                <Label>Sezioni Visibili</Label>
                <p className="text-xs text-muted-foreground">Seleziona le sezioni del portale a cui il docente può accedere</p>
                <div className="grid grid-cols-2 gap-2">
                  {sectionOptions.map(section => (
                    <div key={section.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={section.id}
                        checked={formData.allowed_sections.includes(section.id)}
                        onCheckedChange={() => toggleSection(section.id)}
                      />
                      <label htmlFor={section.id} className="text-sm cursor-pointer">
                        {section.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              
              <Button type="submit" className="w-full">
                {editingTeacher ? 'Salva Modifiche' : 'Crea Docente'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {teachers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg mb-2">Nessun docente</h3>
            <p className="text-muted-foreground">
              {isReadOnly
                ? 'Nessun docente inserito. Crea un utente con ruolo "Docente" dalla pagina Utenti.'
                : 'Aggiungi il primo docente per iniziare'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {teachers.map((teacher, index) => (
            <motion.div
              key={teacher.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="h-full">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="font-semibold text-primary text-lg">
                        {teacher.name?.[0]?.toUpperCase() || '?'}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      {!isReadOnly && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(teacher)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(teacher.id)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <h3 className="font-semibold text-lg mb-1">{teacher.name}</h3>
                  
                  {/* v88: UN SOLO upload "Firma e Timbro" combinato (come richiesto utente) */}
                  <div className="mt-3 mb-3">
                    <SignatureSlot
                      teacher={teacher}
                      type="firma"
                      label="Firma e Timbro (immagine unica)"
                      onChange={loadTeachers}
                      readOnly={false}
                    />
                  </div>
                  
                  {teacher.specialization && (
                    <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
                      <Award className="w-4 h-4" />
                      {teacher.specialization}
                    </div>
                  )}
                  
                  {teacher.email && (
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Mail className="w-4 h-4" />
                      {teacher.email}
                    </div>
                  )}
                  
                  {teacher.phone && (
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Phone className="w-4 h-4" />
                      {teacher.phone}
                    </div>
                  )}
                  
                  {teacher.bio && (
                    <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{teacher.bio}</p>
                  )}
                  
                  {teacher.allowed_sections && teacher.allowed_sections.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
                        <Eye className="w-3 h-3" />
                        Sezioni visibili:
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {teacher.allowed_sections.map(section => (
                          <span key={section} className="px-2 py-0.5 bg-primary/10 rounded text-xs">
                            {sectionOptions.find(s => s.id === section)?.label || section}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

// v83: SignatureSlot — UI per upload firma/timbro del docente
function SignatureSlot({ teacher, type, label, onChange, readOnly }) {
  const [uploading, setUploading] = useState(false);
  const url = teacher[`${type}_url`];
  const apiBase = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Max 2 MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/teachers/${teacher.id}/${type}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${label} caricata`);
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Errore upload ${label}`);
    } finally { setUploading(false); }
  };

  const onDelete = async () => {
    if (!window.confirm(`Rimuovere ${label.toLowerCase()}?`)) return;
    try {
      await api.delete(`/teachers/${teacher.id}/${type}`);
      toast.success(`${label} rimossa`);
      onChange?.();
    } catch { toast.error('Errore'); }
  };

  return (
    <div className="border border-dashed rounded-md p-2 bg-slate-50">
      <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">{label}</p>
      {url ? (
        <div className="relative group">
          <img src={apiBase + url.replace('/api','')} alt={label} className="w-full h-20 object-contain bg-white rounded border" />
          {!readOnly && (
            <button onClick={onDelete}
              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 text-[10px] opacity-0 group-hover:opacity-100" data-testid={`delete-${type}-${teacher.id}`}>×</button>
          )}
        </div>
      ) : (
        readOnly ? (
          <p className="text-[10px] text-slate-400 italic h-20 flex items-center justify-center">Nessuna</p>
        ) : (
          <label className="cursor-pointer block">
            <div className="h-20 flex items-center justify-center text-[11px] text-slate-500 bg-white border rounded hover:bg-emerald-50 hover:border-emerald-300">
              {uploading ? 'Caricamento...' : `+ Carica ${label.toLowerCase()}`}
            </div>
            <input type="file" accept="image/*" className="hidden"
              onChange={onUpload} disabled={uploading}
              data-testid={`upload-${type}-${teacher.id}`} />
          </label>
        )
      )}
    </div>
  );
}

